import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, Leaf, Wind, Archive, Droplets, Zap, HardHat, Shield, Star, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const iconMap = { Leaf, Wind, Archive, Droplets, Zap, HardHat };

export default function Home() {
  const { lang, t } = useLanguage();
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    document.title = 'Orange Onderhoudsbedrijf | Onderhoud voor Particulieren, Bedrijven & Gemeenten';
    Promise.all([
      axios.get(`${API}/services`),
      axios.get(`${API}/projects`),
      axios.get(`${API}/content`),
    ]).then(([sv, pj, ct]) => {
      setServices(sv.data.slice(0, 6));
      setProjects(pj.data.slice(0, 3));
      setContent(ct.data);
    }).catch(() => {});
  }, []);

  const heroTitle = content?.hero?.title?.[lang] || t('home.heroTitle');
  const heroSubtitle = content?.hero?.subtitle?.[lang] || t('home.heroSubtitle');

  return (
    <div className="bg-[#F8FAFC]">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#F8FAFC] via-[#F8FAFC]/97 via-60% to-[#F8FAFC]/40" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-24 py-20 w-full">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-[#FF5F00] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 animate-fade-in-up">
              <div className="w-1.5 h-1.5 bg-[#FF5F00] rounded-full animate-pulse" />
              Orange Onderhoudsbedrijf
            </span>
            <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] leading-tight mb-6 animate-fade-in-up animate-delay-100">
              {heroTitle.split(' ').map((word, i) => {
                const w = word.toLowerCase().replace(/[&,]/g, '');
                const highlight = ['gemeente', 'municipality', 'municipio', 'handyman', 'oplossingen', 'solutions', 'soluciones'].includes(w);
                return highlight
                  ? <span key={i} className="text-[#FF5F00]">{word} </span>
                  : <span key={i}>{word} </span>;
              })}
            </h1>
            <p className="text-lg text-[#0F172A] font-medium leading-relaxed mb-8 animate-fade-in-up animate-delay-200 max-w-xl bg-white/70 rounded-xl px-4 py-3 backdrop-blur-sm">
              {heroSubtitle}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animate-delay-300">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#FF5F00] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#EA580C] transition-all shadow-lg shadow-orange-200 active:scale-95"
              >
                {t('home.heroCta')} <ArrowRight size={18} />
              </Link>
              <Link
                to="/diensten"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-[#334155] px-8 py-4 rounded-xl font-bold hover:border-[#FF5F00] hover:text-[#FF5F00] transition-all"
              >
                {t('home.heroCta2')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section data-testid="stats-section" className="bg-white border-y border-slate-100">
        <div className="px-6 md:px-12 lg:px-24 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-slate-100">
            {[
              { num: t('home.stat1'), label: t('home.stat1Label') },
              { num: t('home.stat2'), label: t('home.stat2Label') },
              { num: t('home.stat3'), label: t('home.stat3Label') },
              { num: t('home.stat4'), label: t('home.stat4Label') },
            ].map((stat, i) => (
              <div key={i} className="text-center px-6">
                <p className="text-3xl font-black text-[#FF5F00]">{stat.num}</p>
                <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="px-6 md:px-12 lg:px-24 py-20">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">{t('nav.services')}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-3 mb-4">{t('home.servicesTitle')}</h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">{t('home.servicesSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon] || Leaf;
            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-orange-200 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <div className="relative h-48 overflow-hidden flex-shrink-0">
                  <img
                    src={service.image_url}
                    alt={service.name?.[lang]}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=60'; }}
                  />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <Icon size={20} strokeWidth={1.5} className="text-[#FF5F00]" />
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-[#0F172A] text-base mb-2">{service.name?.[lang]}</h3>
                  <p className="text-[#64748B] text-sm leading-relaxed">
                    {service.description?.[lang]}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/diensten"
            data-testid="view-all-services"
            className="inline-flex items-center gap-2 text-[#FF5F00] font-bold hover:gap-3 transition-all"
          >
            {t('nav.services')} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-section" className="bg-[#0F172A] px-6 md:px-12 lg:px-24 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3">{t('home.whyTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Shield, title: t('home.benefit1Title'), desc: t('home.benefit1Desc') },
            { icon: Star, title: t('home.benefit2Title'), desc: t('home.benefit2Desc') },
            { icon: Clock, title: t('home.benefit3Title'), desc: t('home.benefit3Desc') },
          ].map((b, i) => (
            <div key={i} data-testid={`benefit-card-${i}`} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-orange-500/40 transition-colors">
              <div className="w-14 h-14 bg-[#FF5F00]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <b.icon size={28} className="text-[#FF5F00]" strokeWidth={1.5} />
              </div>
              <h3 className="font-bold text-white text-xl mb-3">{b.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Projects */}
      {projects.length > 0 && (
        <section data-testid="projects-preview-section" className="px-6 md:px-12 lg:px-24 py-20">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">{t('nav.projects')}</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mt-3">{t('home.projectsTitle')}</h2>
              <p className="text-[#64748B] mt-2">{t('home.projectsSubtitle')}</p>
            </div>
            <Link
              to="/projecten"
              data-testid="view-all-projects"
              className="hidden md:inline-flex items-center gap-2 text-[#FF5F00] font-bold hover:gap-3 transition-all whitespace-nowrap"
            >
              {t('home.viewAll')} <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.id} data-testid={`project-card-${project.id}`} className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all group">
                <div className="relative h-56 overflow-hidden bg-slate-100">
                  {project.images?.[0] ? (
                    <img
                      src={project.images[0]}
                      alt={project.title?.[lang]}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=60'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <HardHat size={40} className="text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#0F172A] mb-1">{project.title?.[lang]}</h3>
                  <div className="flex items-center gap-4 text-xs text-[#64748B]">
                    <span>{project.location}</span>
                    <span>{project.year}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="cta-section" className="px-6 md:px-12 lg:px-24 py-20">
        <div className="bg-gradient-to-br from-[#FF5F00] to-[#EA580C] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 0%, transparent 60%)' }} />
          <div className="relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{t('home.ctaTitle')}</h2>
            <p className="text-orange-100 text-lg mb-8 max-w-xl mx-auto">{t('home.ctaSubtitle')}</p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 bg-white text-[#FF5F00] px-10 py-4 rounded-xl font-black text-lg hover:bg-orange-50 transition-all shadow-lg active:scale-95"
            >
              {t('home.ctaButton')} <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
